import React, { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      full_name: "",
      phone: "",
      email: "",
      property_type: "",
      property_address: "",
      county_state: "",
      message: "",
      consent: false,
      website: "", // honeypot
    },
  });

  const [status, setStatus] = useState({ state: "idle", error: "" });

  const onSubmit = async (data) => {
    setStatus({ state: "submitting", error: "" });
    try {
      const res = await axios.post(`${API}/leads`, data, { timeout: 15000 });
      if (res.data && res.data.status === "success") {
        setStatus({ state: "success", error: "" });
        reset();
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your request. Please try again.";
      setStatus({ state: "error", error: msg });
    }
  };

  return (
    <section
      id="offer-form"
      className="section-pad relative"
      style={{ background: "#FAF7F2" }}
      data-testid="lead-form-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: persuasion */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <p className="eyebrow">Get Started</p>
            <h2
              className="font-heading font-medium mt-4"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.4rem)",
                lineHeight: 1.1,
                color: "#122B20",
                letterSpacing: "-0.015em",
              }}
            >
              Get Your{" "}
              <span className="gold-italic">No-Obligation</span> Cash Offer.
            </h2>
            <p
              className="mt-6 font-body text-[#2B2926]/85 max-w-lg"
              style={{ fontSize: "1.05rem", lineHeight: 1.75 }}
            >
              Two minutes now. An offer within 48 hours. Zero pressure ever.
              Even if you’re just curious what your land is worth — we’re happy
              to run the numbers.
            </p>

            <ul className="mt-8 space-y-3 max-w-md">
              {[
                "Free & confidential",
                "No spam, no robocalls",
                "Offer in 48 hours",
              ].map((line) => (
                <li key={line} className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(198,161,91,0.15)" }}
                  >
                    <CheckCircle2 size={14} color="#C6A15B" strokeWidth={2} />
                  </div>
                  <span
                    className="font-body text-[#2B2926]/90"
                    style={{ fontSize: "0.95rem" }}
                  >
                    {line}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div
              className="glass-light p-6 md:p-10 relative"
              data-testid="lead-form-card"
            >
              {status.state === "success" ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="py-8 text-center"
                  data-testid="lead-form-success"
                >
                  <div className="mx-auto flex items-center justify-center">
                    <motion.div
                      initial={{ scale: 0.4, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        duration: 0.6,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      className="w-20 h-20 rounded-full flex items-center justify-center"
                      style={{
                        background: "rgba(27,67,50,0.08)",
                        border: "2px solid #1B4332",
                      }}
                    >
                      <CheckCircle2
                        size={44}
                        color="#1B4332"
                        strokeWidth={1.8}
                      />
                    </motion.div>
                  </div>
                  <h3
                    className="font-heading font-medium mt-6"
                    style={{ color: "#122B20", fontSize: "1.75rem" }}
                  >
                    Got it — we’re <span className="gold-italic">on it.</span>
                  </h3>
                  <p
                    className="mt-4 font-body text-[#2B2926]/80 max-w-md mx-auto"
                    style={{ fontSize: "1rem", lineHeight: 1.7 }}
                  >
                    We’ll reach out within one business day, and you’ll have
                    your offer within 48 hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus({ state: "idle", error: "" })}
                    className="btn-ghost mt-8"
                    data-testid="lead-form-reset"
                  >
                    Submit another property
                  </button>
                </motion.div>
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  data-testid="lead-form"
                  className="space-y-5"
                >
                  {/* Honeypot */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      width: 1,
                      height: 1,
                      overflow: "hidden",
                    }}
                  >
                    <label>
                      Website
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        {...register("website")}
                      />
                    </label>
                  </div>

                  <div className="field">
                    <input
                      id="full_name"
                      type="text"
                      placeholder=" "
                      autoComplete="name"
                      data-testid="input-full-name"
                      {...register("full_name", {
                        required: "Please tell us your name.",
                        minLength: {
                          value: 2,
                          message: "Name looks a little short.",
                        },
                      })}
                    />
                    <label htmlFor="full_name">Full name*</label>
                    {errors.full_name && (
                      <p className="error-text" data-testid="error-full-name">
                        {errors.full_name.message}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="field">
                      <input
                        id="phone"
                        type="tel"
                        placeholder=" "
                        autoComplete="tel"
                        data-testid="input-phone"
                        {...register("phone", {
                          required: "Phone number is required.",
                          validate: (v) => {
                            const digits = (v || "").replace(/\D/g, "");
                            return digits.length >= 7 || "That doesn’t look like a phone number.";
                          },
                        })}
                      />
                      <label htmlFor="phone">Phone*</label>
                      {errors.phone && (
                        <p className="error-text" data-testid="error-phone">
                          {errors.phone.message}
                        </p>
                      )}
                    </div>
                    <div className="field">
                      <input
                        id="email"
                        type="email"
                        placeholder=" "
                        autoComplete="email"
                        data-testid="input-email"
                        {...register("email", {
                          required: "Email is required.",
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Please enter a valid email.",
                          },
                        })}
                      />
                      <label htmlFor="email">Email*</label>
                      {errors.email && (
                        <p className="error-text" data-testid="error-email">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="field">
                    <input
                      id="property_address"
                      type="text"
                      placeholder=" "
                      data-testid="input-property-address"
                      {...register("property_address", {
                        required: "We need a location to look up the property.",
                      })}
                    />
                    <label htmlFor="property_address">
                      Property address or Parcel/APN*
                    </label>
                    <p
                      className="mt-1.5 text-xs"
                      style={{ color: "#A8A29A" }}
                    >
                      An address, parcel number, or even a county + road name works.
                    </p>
                    {errors.property_address && (
                      <p className="error-text" data-testid="error-property-address">
                        {errors.property_address.message}
                      </p>
                    )}
                  </div>

                  <div className="field">
                    <input
                      id="county_state"
                      type="text"
                      placeholder=" "
                      data-testid="input-county-state"
                      {...register("county_state", {
                        required: "County & State helps us pull comps.",
                      })}
                    />
                    <label htmlFor="county_state">County &amp; State*</label>
                    {errors.county_state && (
                      <p className="error-text" data-testid="error-county-state">
                        {errors.county_state.message}
                      </p>
                    )}
                  </div>

                  <div className="field">
                    <select
                      id="property_type"
                      data-testid="input-property-type"
                      defaultValue=""
                      style={{
                        width: "100%",
                        background: "rgba(250,247,242,0.55)",
                        border: "1px solid rgba(168,162,154,0.45)",
                        borderRadius: "0.85rem",
                        padding: "1.25rem 1rem 0.55rem",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "0.95rem",
                        color: "#2B2926",
                        outline: "none",
                        appearance: "none",
                        WebkitAppearance: "none",
                        MozAppearance: "none",
                      }}
                      {...register("property_type")}
                    >
                      <option value="" disabled hidden>&nbsp;</option>
                      <option value="House">House</option>
                      <option value="Land or Lot">Land or Lot</option>
                      <option value="Other">Other</option>
                    </select>
                    <label
                      htmlFor="property_type"
                      style={{
                        position: "absolute",
                        top: "0.5rem",
                        left: "1.05rem",
                        color: "#1B4332",
                        fontSize: "0.68rem",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        pointerEvents: "none",
                      }}
                    >
                      Property Type
                    </label>
                    <div
                      className="absolute pointer-events-none"
                      style={{ right: "1rem", top: "1.15rem", color: "#A8A29A" }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                  </div>

                  <div className="field">
                    <textarea
                      id="message"
                      placeholder=" "
                      rows={4}
                      data-testid="input-message"
                      {...register("message")}
                    />
                    <label htmlFor="message">Tell us anything else</label>
                    <p
                      className="mt-1.5 text-xs"
                      style={{ color: "#A8A29A" }}
                    >
                      Acreage, situation, timeline — whatever you’d like us to know.
                    </p>
                  </div>

                  <label
                    className="flex items-start gap-3 pt-1 cursor-pointer select-none"
                    data-testid="consent-label"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 accent-[#1B4332]"
                      data-testid="input-consent"
                      {...register("consent", {
                        required:
                          "Please confirm you agree to be contacted so we can send your offer.",
                      })}
                    />
                    <span
                      className="font-body text-[#2B2926]/85"
                      style={{ fontSize: "0.88rem", lineHeight: 1.6 }}
                    >
                      I agree to be contacted by Ibex Acquisitions &amp;
                      Development about my property.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="error-text" data-testid="error-consent">
                      {errors.consent.message}
                    </p>
                  )}

                  {status.state === "error" && (
                    <div
                      className="flex items-start gap-3 p-4 rounded-xl"
                      style={{
                        background: "rgba(138, 43, 43, 0.08)",
                        border: "1px solid rgba(138, 43, 43, 0.3)",
                      }}
                      data-testid="lead-form-error"
                    >
                      <AlertCircle
                        size={18}
                        color="#8a2b2b"
                        strokeWidth={2}
                        className="mt-0.5 shrink-0"
                      />
                      <p
                        className="font-body text-[#8a2b2b]"
                        style={{ fontSize: "0.9rem", lineHeight: 1.6 }}
                      >
                        {status.error} Please try again in a moment.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || status.state === "submitting"}
                    className="btn-primary w-full mt-2 disabled:opacity-70 disabled:cursor-wait"
                    data-testid="lead-form-submit"
                  >
                    {isSubmitting || status.state === "submitting" ? (
                      <span>Sending…</span>
                    ) : (
                      <>
                        <span>Get My Cash Offer</span>
                        <ArrowRight size={18} strokeWidth={1.75} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LeadForm;
